<impersonation-end-modal>
    <!--
      Dedicated modal for ending / pausing an impersonation session.
      Used by both the impersonation banner (when the admin is currently
      inside the session) and the Active Impersonations card on admin.tag
      + Customer 360 → Content (when the admin wants to terminate any
      session they own, whether or not they're currently in it).

      Three actions:
        - Cancel: dismiss, do nothing.
        - Pause Session (optional, shown when showPause === true):
            Drops the imp_ token locally and returns the viewer to their
            admin session. Server-side the session stays alive so it can
            be resumed from the Active Impersonations card later.
        - End Impersonation Session:
            Terminates the session on the server; localStorage + Redux
            clean-up is the caller's responsibility (so the banner can
            do its reload dance).

      show() resolves with 'cancel' | 'pause' | 'end' so callers branch
      without needing a separate callback per action.
    -->
    <dialog ref="dialog" class="modal" aria-labelledby="impersonation-end-modal-heading">
        <div class="modal-box">
            <h3 id="impersonation-end-modal-heading" class="font-bold text-lg mb-2 flex items-center gap-2">
                <i class="icon-user-check text-warning" aria-hidden="true"></i>
                <span>{getHeadingText()}</span>
            </h3>
            <p class="text-sm text-base-content/80 mb-3">{state.message}</p>
            <!-- Contextual explainer only when Pause is an option — users
                 who can only End don't need to be told the difference. -->
            <div if="{state.showPause}" class="alert alert-info text-sm mb-2">
                <i class="icon-metalark-info"></i>
                <span>{locale?.t('impersonation_end_modal_pause_explainer')}</span>
            </div>
            <div class="modal-action">
                <button class="btn btn-ghost" onclick="{handleCancel}">
                    {locale?.t('cancel')}
                </button>
                <button if="{state.showPause}"
                    class="btn btn-warning"
                    onclick="{handlePause}">
                    <!-- icon-pause_circle_filled comes from the base Material
                         icon stylesheet; no metalark-prefixed pause glyph is
                         mapped, and this reads cleanly next to the label. -->
                    <i class="icon-pause_circle_filled text-lg me-1"></i>
                    {locale?.t('impersonation_end_modal_pause')}
                </button>
                <button class="btn btn-error" onclick="{handleEnd}">
                    <i class="icon-metalark-x me-1"></i>
                    {locale?.t('admin_impersonation_endsession')}
                </button>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button aria-label="{locale?.t('close')}">{locale?.t('close')}</button>
        </form>
    </dialog>

    <script>
        export default {
            state: {
                title: '',
                message: '',
                showPause: false,
                // Promise resolver for the current show() call; replaced on
                // each open so stale resolvers from cancelled dialogs don't
                // double-fire.
                onResult: null,
            },

            getHeadingText() {
                return this.state.title
                    || this.locale?.t('admin_impersonation_endsession')
                    || 'End Impersonation Session';
            },

            // Returns a Promise<'cancel' | 'pause' | 'end'>.
            show({ title, message, showPause = false } = {}) {
                return new Promise((resolve) => {
                    this.update({
                        title: title || this.locale?.t('admin_impersonation_endsession') || 'End Impersonation Session',
                        message: message || '',
                        showPause: !!showPause,
                        onResult: resolve,
                    });
                    const dlg = this.$refs?.dialog || this.root.querySelector('dialog');
                    if (dlg && typeof dlg.showModal === 'function') {
                        dlg.showModal();
                    }
                });
            },

            closeDialog() {
                const dlg = this.$refs?.dialog || this.root.querySelector('dialog');
                if (dlg && typeof dlg.close === 'function') {
                    dlg.close();
                }
            },

            handleCancel() {
                this.closeDialog();
                if (this.state.onResult) {
                    this.state.onResult('cancel');
                    this.state.onResult = null;
                }
            },

            handlePause() {
                this.closeDialog();
                if (this.state.onResult) {
                    this.state.onResult('pause');
                    this.state.onResult = null;
                }
            },

            handleEnd() {
                this.closeDialog();
                if (this.state.onResult) {
                    this.state.onResult('end');
                    this.state.onResult = null;
                }
            },
        };
    </script>
</impersonation-end-modal>
